/**
 * @file logger.ts
 * @description Conditional logger for admin users
 * @author XiaVan Team
 * 
 * Features:
 * - If admin: Detailed logs with extra info (user data, timestamps, stack trace)
 * - If regular user: Simple logs
 * - If not authenticated: No logs (silent)
 * 
 * Usage:
 * import { logger } from '@/lib/logger'
 * logger.success('User deleted', { email, role })
 * logger.error('Failed to delete', error, { context: 'UserManagement' })
 */

import type { User } from '@/types/user'

/**
 * Logger configuration
 */
interface LoggerConfig {
  currentUser: User | null
  enabled: boolean
}

const config: LoggerConfig = {
  currentUser: null,
  enabled: true,
}

/**
 * Initialize logger with current user
 * Call this in AuthContext when user changes
 */
export function initLogger(user: User | null) {
  config.currentUser = user
  config.enabled = !!user
}

/**
 * Check if current user is admin
 */
function isAdmin(): boolean {
  return config.currentUser?.role === 'admin'
}

/**
 * Format timestamp for logs
 */
function timestamp(): string {
  return new Date().toISOString()
}

/**
 * Log success message (green)
 * Admin: Detailed with extra data
 * User: Simple message
 */
export function logSuccess(message: string, data?: unknown) {
  if (!config.enabled) return

  if (isAdmin()) {
    console.log(
      `%c✅ ${message}`,
      'color: #10b981; font-weight: bold',
      '\n📊 Data:', data,
      '\n🕐 Time:', timestamp(),
      '\n👤 User:', config.currentUser?.email
    )
  } else {
    console.log(`✅ ${message}`)
  }
}

/**
 * Log error message (red)
 * Admin: Detailed with stack trace and context
 * User: Simple error message
 */
export function logError(message: string, error?: unknown, context?: Record<string, unknown>) {
  if (!config.enabled) return

  if (isAdmin()) {
    console.error(
      `%c❌ ${message}`,
      'color: #ef4444; font-weight: bold',
      '\n🐛 Error:', error,
      '\n📍 Context:', context,
      '\n🕐 Time:', timestamp(),
      '\n👤 User:', config.currentUser?.email
    )
    if (error instanceof Error && error.stack) {
      console.error('📚 Stack trace:', error.stack)
    }
  } else {
    console.error(`❌ ${message}`)
  }
}

/**
 * Log warning message (yellow)
 * Admin: Detailed
 * User: Simple
 */
export function logWarning(message: string, data?: unknown) {
  if (!config.enabled) return

  if (isAdmin()) {
    console.warn(
      `%c⚠️ ${message}`,
      'color: #f59e0b; font-weight: bold',
      '\n📊 Data:', data,
      '\n🕐 Time:', timestamp(),
      '\n👤 User:', config.currentUser?.email
    )
  } else {
    console.warn(`⚠️ ${message}`)
  }
}

/**
 * Log info message (blue)
 * Admin: Detailed
 * User: Simple
 */
export function logInfo(message: string, data?: unknown) {
  if (!config.enabled) return

  if (isAdmin()) {
    console.info(
      `%cℹ️ ${message}`,
      'color: #3b82f6; font-weight: bold',
      '\n📊 Data:', data,
      '\n🕐 Time:', timestamp(),
      '\n👤 User:', config.currentUser?.email
    )
  } else {
    console.info(`ℹ️ ${message}`)
  }
}

/**
 * Grouped logger functions
 */
export const logger = {
  success: logSuccess,
  error: logError,
  warning: logWarning,
  info: logInfo,
  init: initLogger,
}
